// Simple CHANGELOG.md parser
// Parses markdown changelog following Keep a Changelog format

export interface ChangelogRelease {
  version: string;
  date: string | null;
  changes: {
    added?: string[];
    changed?: string[];
    fixed?: string[];
    removed?: string[];
    deprecated?: string[];
    security?: string[];
  };
}

export function parseChangelog(markdownContent: string): ChangelogRelease[] {
  const releases: ChangelogRelease[] = [];
  const lines = markdownContent.split("\n");

  let currentRelease: ChangelogRelease | null = null;
  let currentCategory: keyof ChangelogRelease["changes"] | null = null;

  for (const line of lines) {
    // Match version headers like "## [1.0.16]" or "## [1.0.16] - 2025-12-05"
    const versionMatch = line.match(/^##\s+\[(\d+\.\d+\.\d+)\](?:\s+-\s+(.+))?/);
    if (versionMatch) {
      // Save previous release
      if (currentRelease) {
        releases.push(currentRelease);
      }

      // Start new release
      currentRelease = {
        version: versionMatch[1],
        date: versionMatch[2] || null,
        changes: {},
      };
      currentCategory = null;
      continue;
    }

    // Match category headers like "### Added", "### Fixed", etc.
    const categoryMatch = line.match(/^###\s+(Added|Changed|Fixed|Removed|Deprecated|Security)/i);
    if (categoryMatch && currentRelease) {
      currentCategory = categoryMatch[1].toLowerCase() as keyof ChangelogRelease["changes"];
      if (!currentRelease.changes[currentCategory]) {
        currentRelease.changes[currentCategory] = [];
      }
      continue;
    }

    // Match list items like "- Some change"
    const itemMatch = line.match(/^-\s+(.+)/);
    if (itemMatch && currentRelease && currentCategory) {
      const item = itemMatch[1].trim();
      if (item) {
        currentRelease.changes[currentCategory]!.push(item);
      }
    }
  }

  // Don't forget the last release
  if (currentRelease) {
    releases.push(currentRelease);
  }

  return releases;
}
